import * as PIXI from 'pixi.js';
import isEqual from 'lodash/isEqual';
import type { BaseAssetSettings, BaseAsset } from '../BaseAsset';
import { assertArray, assertNumberBetween } from '../../../validators';
import { getAttributesToSerialize, NumberAttribute, StringAttribute } from '../../attributes';
import { unicodeToChar, distance } from '../../../libs/other';
import { BaseTextAsset } from '../BaseTextAsset';
import type { AssetGroup } from '../AssetGroup';
import {
    getTextBoxHeightGovernor,
    isHeightHeld,
    resolveIsHeightMatchingText,
    resolveTextBoxHeightState,
} from '../utils';
import type { TextDirection } from '../../../enums';
import { SeekMode, TextBoxHeightState } from '../../../enums';
import type { Coordinate, Pixels } from '../../../types';
import { Config } from '../../../video-data/config';
import { ErrorReporter } from '../../../libs/error-reporter';
import type { RawAssetData } from '../../../video-data/types';
import { MorphManager, type MorphLinkedPropertySpecs } from '../../morph/MorphManager';
import { holdConfigCurveAtRest } from '../../../libs/AnimationCurveManager';
import { buildMetricsForText, type TextMetrics } from './metrics/TextMetrics';
import { canMeasureText, getHalfDiagonal } from './textMetricsHelpers';
import { CanvasFontSizeToFitAllTextBinarySearch } from './metrics/FontSizeToFitAllTextBinarySearch';
import { TextStyleRenderer } from './renderer/TextStyleRenderer';
import { TextRenderer } from './renderer/TextRenderer';
import type { TextRendererSettings } from './renderer/TextRenderer';
import { ANIMATION_CONFIGURATIONS_KEY_IN_SERIALIZED_DATA, TextAssetStyle } from './TextAssetStyle';
import { TextEditingManager } from './asset/TextEditingManager';
import { TextAssetDynamicCircle } from './asset/TextAssetDynamicCircle';

export type TextEditingOptions = {
    minSize?: number;
    initialClickEvent?: MouseEvent;
    outlineColor?: string;
};

export type TextAssetSettings = BaseAssetSettings;

/**
 * A set of properties that exist on the normal and highlight styles that can be
 * edited by the user via controls exposed in the video creator
 */
type TextEditingStyles = {
    /**
     * A localized value relative to the video's aspect ratio and other configuration
     */
    fontSize?: number;
};

const DefaultTextEditingOptions: TextEditingOptions = {
    minSize: 40,
};
const saveWithoutMorphableProperties = (textAssetStyle: TextAssetStyle) => {
    return {
        ...textAssetStyle.save(),
        [ANIMATION_CONFIGURATIONS_KEY_IN_SERIALIZED_DATA]: [],
    };
};
const syncWithoutClobberingMorphableProperties = (
    target: TextAssetStyle,
    source: TextAssetStyle,
) => {
    const sourceWithoutAnimations = saveWithoutMorphableProperties(source);
    const targetAnimations = target.save()[ANIMATION_CONFIGURATIONS_KEY_IN_SERIALIZED_DATA];
    target.loadEditable({
        ...sourceWithoutAnimations,
        [ANIMATION_CONFIGURATIONS_KEY_IN_SERIALIZED_DATA]: targetAnimations,
    });
};

const TEXT_MORPH_LINKED_SPECS: MorphLinkedPropertySpecs<TextAsset> = {
    text: {},
    textSize: {},
    textHighlights: {
        isEqual: (a, b) => isEqual(a.textHighlights, b.textHighlights),
        sync: (target, source) => {
            target.textHighlights = source.textHighlights.map((r) => [...r] as [number, number]);
        },
    },
    textBold: {
        isEqual: (a, b) => isEqual(a.textBold, b.textBold),
        sync: (target, source) => {
            target.textBold = source.textBold.map((r) => [...r] as [number, number]);
        },
    },
    textItalic: {
        isEqual: (a, b) => isEqual(a.textItalic, b.textItalic),
        sync: (target, source) => {
            target.textItalic = source.textItalic.map((r) => [...r] as [number, number]);
        },
    },
    textUnderline: {
        isEqual: (a, b) => isEqual(a.textUnderline, b.textUnderline),
        sync: (target, source) => {
            target.textUnderline = source.textUnderline.map((r) => [...r] as [number, number]);
        },
    },
    normalStyle: {
        isEqual: (a, b) =>
            isEqual(
                saveWithoutMorphableProperties(a.normalStyle),
                saveWithoutMorphableProperties(b.normalStyle),
            ),
        sync: (target, source) =>
            syncWithoutClobberingMorphableProperties(target.normalStyle, source.normalStyle),
    },
    highlightStyle: {
        isEqual: (a, b) =>
            isEqual(
                saveWithoutMorphableProperties(a.highlightStyle),
                saveWithoutMorphableProperties(b.highlightStyle),
            ),
        sync: (target, source) =>
            syncWithoutClobberingMorphableProperties(target.highlightStyle, source.highlightStyle),
    },
};

export class TextAsset extends BaseTextAsset<TextRenderer> {
    override _classType = Config.TEXT_ASSET_TYPE;

    @StringAttribute()
    accessor text = '';
    /**
     * Highlight text scale multiplier based on the normal style font size. This is used to automatically change the
     * font size of the highlighted text when changing the normal text font size which happens when the user changes
     * the font size manually.
     **/
    @NumberAttribute()
    accessor highlightedTextScale = 1.0;

    #sanitizedText = '';

    #initialTextAndHeightStates: {
        sanitizedText: string;
        isThisHeightFixedByHeightGovernor: boolean;
    } | null = null;
    #highlightAreas: [number, number][] = [];
    #boldRanges: [number, number][] = [];
    #italicRanges: [number, number][] = [];
    #underlineRanges: [number, number][] = [];

    #styles: {
        normalStyle: TextAssetStyle;
        highlightStyle: TextAssetStyle;
    };

    #textEditingManager: TextEditingManager | null = null;

    /**
     * @internal
     */
    _textEditingOptions: TextEditingOptions = { ...DefaultTextEditingOptions };

    // TODO(strict-ts): Change container type when BaseAsset.ts is updated
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    constructor(container: any, settings: TextAssetSettings) {
        const renderer = new TextRenderer();
        super(container, settings, renderer);

        const styleSettings = {
            parent: this,
            autoUpdate: true,
            referenceToOutputMultiplier: this.referenceToOutputMultiplier,
            getReferenceResolution: () => this.getReferenceResolution(),
            getOutputResolution: () => this.getOutputResolution(),
            canFitGraphicsToText: true,
        };
        const normalStyle = new TextAssetStyle({ ...styleSettings, name: 'normal' });
        const highlightStyle = new TextAssetStyle({ ...styleSettings, name: 'highlight' });

        // By default, render highlights as part of the first render pass
        normalStyle.renderHighlighted = true;
        normalStyle.renderHighlightedColor = true;
        highlightStyle.renderHighlighted = false;
        // This doesn't actually have any effect. Once CanvasTextAsset and TextAsset are consolidated, we should
        // consider removing renderHighlightedColor from the CanvasTextAssetStyle and make it a CanvasTextAsset property
        highlightStyle.renderHighlightedColor = true;

        this.#styles = { normalStyle, highlightStyle };
    }

    get styles() {
        return {
            normalStyle: this.normalStyle,
            highlightStyle: this.highlightStyle,
        };
    }

    get textHighlights(): readonly [number, number][] {
        return this.#highlightAreas;
    }

    set textHighlights(value: readonly [number, number][]) {
        assertArray(value);
        if (!isEqual(this.#highlightAreas, value)) {
            this.#highlightAreas = value.slice();
            this.requiresRedraw = true;
        }
    }

    get textBold(): readonly [number, number][] {
        return this.#boldRanges;
    }

    set textBold(value: readonly [number, number][]) {
        assertArray(value);
        if (!isEqual(this.#boldRanges, value)) {
            this.#boldRanges = value.slice();
            this.requiresRedraw = true;
        }
    }

    get textItalic(): readonly [number, number][] {
        return this.#italicRanges;
    }

    set textItalic(value: readonly [number, number][]) {
        assertArray(value);
        if (!isEqual(this.#italicRanges, value)) {
            this.#italicRanges = value.slice();
            this.requiresRedraw = true;
        }
    }

    get textUnderline(): readonly [number, number][] {
        return this.#underlineRanges;
    }

    set textUnderline(value: readonly [number, number][]) {
        assertArray(value);
        if (!isEqual(this.#underlineRanges, value)) {
            this.#underlineRanges = value.slice();
            this.requiresRedraw = true;
        }
    }

    get _wordElements() {
        return (
            this.renderer.metrics?.lines.map((line) => line.words.map((word) => word.elements)) ??
            []
        );
    }

    get textLinesForEditing() {
        return this.renderer.metrics?.lines ?? [];
    }

    get #stylesList(): TextAssetStyle[] {
        return Object.values(this.#styles);
    }

    get normalStyle(): TextAssetStyle {
        return this.#styles.normalStyle;
    }

    get highlightStyle(): TextAssetStyle {
        return this.#styles.highlightStyle;
    }

    override _checkAndFixProperties() {
        const sanitizedText = unicodeToChar(this.text);
        const textWasEdited = this.hasTextEditingEnabled && sanitizedText !== this.#sanitizedText;
        this.#sanitizedText = sanitizedText;

        this.#highlightAreas = sanitizeHighlights(this.#sanitizedText, this.#highlightAreas);
        this.#boldRanges = sanitizeHighlights(this.#sanitizedText, this.#boldRanges);
        this.#italicRanges = sanitizeHighlights(this.#sanitizedText, this.#italicRanges);
        this.#underlineRanges = sanitizeHighlights(this.#sanitizedText, this.#underlineRanges);
        this.#checkAndFixAnimationAnchors();

        if (textWasEdited && this._enableEnhancedAssetTransform) {
            // The editing manager swaps the real text for a placeholder while the box is
            // empty, so compare against the text the user sees, not the raw sanitized text.
            const governor = this.#heightGovernor;
            const initialStates = this.#initialTextAndHeightStates;
            const userVisibleText = this.inEmptyState ? '' : this.#sanitizedText;
            if (initialStates && initialStates.sanitizedText === userVisibleText) {
                governor.hasUserChangedMaxHeight = initialStates.isThisHeightFixedByHeightGovernor;
            } else if (!this.inEmptyState && governor.heightState !== TextBoxHeightState.Enlarged) {
                governor.hasUserChangedMaxHeight = false;
            }
        }
    }

    get #heightGovernor(): TextAsset | AssetGroup {
        return getTextBoxHeightGovernor(this);
    }

    /** The box whose height governs what this one draws: its outermost group, or itself. */
    get heightGovernor(): TextAsset | AssetGroup {
        return this.#heightGovernor;
    }

    /** Whether this box's own height governs what it draws, rather than its text doing so. */
    get isHeightHeld(): boolean {
        return isHeightHeld(this);
    }

    /** How `maxHeight` currently stands to the text; see `TextBoxHeightState`. */
    get heightState(): TextBoxHeightState {
        return resolveTextBoxHeightState(
            this,
            this.#heightGovernor,
            this._enableEnhancedAssetTransform,
        );
    }

    get #isHeightMatchingText(): boolean {
        return resolveIsHeightMatchingText(
            this,
            this.#heightGovernor,
            this._enableEnhancedAssetTransform,
        );
    }

    protected override get _occupiedHeight(): number {
        if (!this.#isHeightMatchingText) {
            return this.maxHeight;
        }
        const renderedHeight = this.renderedTextHeight;
        if (renderedHeight === 0) {
            return this.maxHeight;
        }
        return renderedHeight;
    }

    #checkAndFixAnimationAnchors() {
        this.#stylesList.forEach((style) => {
            style.animationConfigurations.forEach((animationConfig) => {
                animationConfig.anchorsToRemove = this._animationAnchorsToRemove;
            });
            style.graphics.forEach((graphic) => {
                graphic.animationConfigurations.forEach((animationConfig) => {
                    animationConfig.anchorsToRemove = this._animationAnchorsToRemove;
                });
            });
        });
    }

    /**
     * Set the font size for the given text to fit within it's current boundary limits.
     *
     * @param wordBreak - when `false` (the default) a word that overflows `maxWidth`
     * counts as "doesn't fit", so the text is shrunk until every word fits on a single
     * line. Pass `true` to preserve the legacy behaviour where the renderer is allowed
     * to force-break long words instead of resizing.
     */
    setFontSizeToFitAllText({ wordBreak = false }: { wordBreak?: boolean } = {}) {
        if (!this.canFitText(this.text, {}, { wordBreak })) {
            const MIN_SIZE_SHIFT = 5;
            const { fontSize } = this.getFontSizeToFitAllText(
                this.text,
                {
                    max: this.textSize - MIN_SIZE_SHIFT,
                    min: this._textEditingOptions.minSize!,
                },
                { wordBreak },
            );
            this.textSize = fontSize;
        }
    }

    /**
     * True when the rendered text exceeds the asset's `maxWidth × maxHeight` bounds. Always
     * `false` unless `capabilities.enableEnhancedAssetTransform` is enabled on this Luminary
     * instance — legacy consumers surface overflow via `inOverflowState` during active editing
     * instead.
     * Editors should consume this getter to display a persistent clipping indicator next to
     * non-legacy text that has overflowed.
     */
    get isClipping(): boolean {
        if (!this._enableEnhancedAssetTransform) {
            return false;
        }
        if (!this.hasText) {
            return false;
        }
        return !this.canFitText(this.text);
    }

    /**
     * The direction in which the text flows. This property affects text metrics.
     */
    get textDirection(): TextDirection {
        return this.normalStyle.textDirection;
    }

    set textDirection(value) {
        if (this.normalStyle.textDirection === value) {
            return;
        }
        this.normalStyle.textDirection = value;
        this.highlightStyle.textDirection = value;
        this.requiresRedraw = true;
    }

    /**
     * @param wordBreak - when `false` (the default) a word that overflows `maxWidth` counts as
     * "doesn't fit", so the size is shrunk until every word fits on a single line. Pass `true` for
     * scripts that don't separate words with spaces (Chinese, Japanese, Thai), where the whole run
     * is a single unbreakable "word": otherwise the max size is capped at fitting the entire run on
     * one line instead of wrapping across the lines the text box actually has room for. Callers that
     * know the text's language (e.g. the web editor) should derive this from that language.
     *
     * The size is measured without `boldRanges`/`italicRanges`/`underlineRanges`, which the shared
     * binary search has no concept of, so it can differ slightly from what `canFitText` reports.
     */
    getFontSizeToFitAllText(
        text = this.text,
        fontSizeRange = { min: 50, max: 250 },
        { wordBreak = false }: { wordBreak?: boolean } = {},
    ) {
        const rendererSettings = this.getRendererSettings();
        // TODO: we create a TextStyle here for getFontSizeToFitAllText which is confusing. We should refactor this.
        const normalStyle = new TextStyleRenderer().apply(rendererSettings.normalStyle);
        const highlightStyle = new TextStyleRenderer().apply(rendererSettings.highlightStyle);

        const { fontSize, displaysAllText } = CanvasFontSizeToFitAllTextBinarySearch.search({
            text: unicodeToChar(text),
            alignment: rendererSettings.alignment,
            normalStyle,
            highlightStyle,
            highlightAreas: rendererSettings.highlightAreas,
            wordBreak,
            maxWidth: rendererSettings.maxTextWidth,
            maxHeight: rendererSettings.maxTextHeight,
            fontSizeRange,
            referenceToOutputMultiplier: this.referenceToOutputMultiplier,
        });

        return {
            fontSize: Math.floor(fontSize),
            displaysAllText,
        };
    }

    get hasText() {
        return this.text.trim().length !== 0;
    }

    override isMorphMatch(other: BaseAsset): boolean {
        const otherText = other as TextAsset;
        if (!Config.isTextAsset(otherText)) {
            return false;
        }

        if (!MorphManager.areLinkedPropertiesEqual(this, otherText, TEXT_MORPH_LINKED_SPECS)) {
            return false;
        }

        return true;
    }

    override syncMorphLinkedProperties(other: BaseAsset): boolean {
        const otherText = other as TextAsset;
        if (!Config.isTextAsset(otherText)) return false;

        return MorphManager.syncLinkedProperties(this, otherText, TEXT_MORPH_LINKED_SPECS);
    }

    // TODO(strict-ts): Change dataObj type when serializableMixin is updated
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    override loadEditable(dataObj: Record<string, any>): this {
        const { normalStyle, highlightStyle, ...restProps } = dataObj;
        if (normalStyle) {
            this.normalStyle.loadEditable(normalStyle);
        }
        if (highlightStyle) {
            this.highlightStyle.loadEditable(highlightStyle);
        }
        super.loadEditable(restProps);
        return this;
    }

    override getRendererSettings(): TextRendererSettings {
        this._syncFontSizeAndLeadingToStyles();
        const baseSettings = super.getRendererSettings();
        const renderSettings = {
            ...baseSettings,
            maxTextHeight: this.#isHeightMatchingText
                ? ((this.getReferenceResolution().height *
                      this.referenceToOutputMultiplier) as Pixels)
                : baseSettings.maxTextHeight,
            text: this.#sanitizedText,
            alignment: this.textAlign,
            highlightAreas: this.#highlightAreas,
            boldRanges: this.#boldRanges,
            italicRanges: this.#italicRanges,
            underlineRanges: this.#underlineRanges,
            wordBreak: true,
            normalStyle: this.normalStyle.getRendererSettings(),
            highlightStyle: this.highlightStyle.getRendererSettings(),
            referenceToOutputMultiplier: this.referenceToOutputMultiplier,
        };
        return renderSettings;
    }

    /**
     * Whether or not text editing mode is enabled
     */
    get hasTextEditingEnabled(): boolean {
        return this.#textEditingManager !== null;
    }

    /**
     * If the user is editing text, whether or not the text asset has no text value and
     * is showing a special empty state
     */
    get inEmptyState(): boolean {
        return this.#textEditingManager?.isShowingEmptyState ?? false;
    }

    /**
     * Returns information used to force the window selection to
     * a default state when a user starts to edit text. If no coordinate is provided,
     * then this method assumes the cursor position will be at the very beginning
     *
     * @param x x-axis click coord, relative to luminary's resolution
     * @param y y-axis click coord, relative to luminary's resolution
     */
    getEditingCursorPosition(x = 0, y = 0): { node: Node; offset: number } | null {
        if (!this.hasTextEditingEnabled) {
            return null;
        }
        const referenceResolution = this.getReferenceResolution();
        assertNumberBetween(x, 0, referenceResolution.width);
        assertNumberBetween(y, 0, referenceResolution.height);

        // Canvas editing handles cursor positioning internally via initialClickEvent.
        // Return the hidden input node so callers that destructure the result don't crash.
        const hiddenInput = this.#textEditingManager?.domContainer.querySelector('#luminary-input');
        return hiddenInput ? { node: hiddenInput, offset: 0 } : null;
    }

    /**
     * If the user is editing the text asset, returns the current window selection as a tuple
     *
     * @return A tuple containing a start and an end index. If they are identical,
     *  that means the selection was collapsed
     */
    getTextInputSelection(): [number, number] | null {
        return this.#textEditingManager?.getSelection() ?? null;
    }

    /**
     * Sets the caret position at the given coordinates when using canvas-based text editing.
     * This should be called after enableTextInput() to position the caret at the click location.
     *
     * @param x The x coordinate in reference resolution space
     * @param y The y coordinate in reference resolution space
     */
    setCaretAtPosition(x: number, y: number) {
        this.#textEditingManager?.setCaretAtPosition(x, y);
    }

    /**
     * Creates HTML nodes used for user interaction to update this text asset's text property. The element
     * gets its default styling, positioning, and event handlers configured here. For each change, the element's
     * children are rebuilt and repositioned to ensure alignment.
     *
     * @param domContainer The DOM container to attach the editing UI to
     * @param options Configuration options
     */
    enableTextInput(domContainer: HTMLElement, options?: TextEditingOptions) {
        if (this.hasTextEditingEnabled) {
            return;
        }

        const mergedOptions = { ...DefaultTextEditingOptions, ...options };
        this._textEditingOptions = mergedOptions;
        this.#initialTextAndHeightStates = {
            sanitizedText: this.#sanitizedText,
            isThisHeightFixedByHeightGovernor: this.#heightGovernor.hasUserChangedMaxHeight,
        };

        try {
            this.#textEditingManager = new TextEditingManager(
                this,
                domContainer,
                mergedOptions.initialClickEvent,
                mergedOptions.outlineColor,
            );
            this.#textEditingManager.handleOverflowState(this.text);
            this.#textEditingManager.updateCaretColor();
            this.requiresRedraw = true;
            this.root._emitChanged();
        } catch (e) {
            if (this.#textEditingManager) {
                this.#textEditingManager.destroy();
            }
            this.#textEditingManager = null;
            throw e;
        }
    }

    /**
     * Disables text editing by removing the input element from the DOM and destroying its reference.
     */
    disableTextInput() {
        if (!this.#textEditingManager) {
            return;
        }

        this.#textEditingManager.destroy();
        this.#textEditingManager = null;
        this.#initialTextAndHeightStates = null;

        if (this.text.trim().length === 0) {
            this.text = '';
        } else {
            this.requiresRedraw = true;
        }

        this.root._emitChanged();
    }

    /**
     * If the user is editing text, whether or not the last change they made was too large
     * for this text asset to fully display
     */
    get inOverflowState(): boolean {
        return this.#textEditingManager?.inOverflowState ?? false;
    }

    /**
     * Updates textSize if asset can fit text with the new text size, otherwise, sets inOverflowState to true.
     */
    tryToUpdateTextSize(value: number) {
        if (this.textSize === value) {
            return;
        }
        if (this._enableEnhancedAssetTransform) {
            this.textSize = value;
            return;
        }
        const canChangeFit = this.canFitText(this.text, {
            fontSize: value,
        });

        if (!canChangeFit) {
            this.#updateOverflowState(true);
            return;
        } else {
            this.#updateOverflowState(false);
        }

        this.textSize = value;
    }

    #updateOverflowState(value: boolean) {
        if (this.#textEditingManager) {
            this.#textEditingManager.inOverflowState = value;
        }
    }

    /**
     * Fires a custom web event notifying listeners that an unrecoverable error has occurred while
     * editing, logs the error, and exits edit mode
     */
    #handleEditingException(e: unknown) {
        if (this.#textEditingManager) {
            const webEvent = new Event(TextEditingManager.NTE_EVENT_EDITING_EXCEPTION);
            this.#textEditingManager.domContainer.dispatchEvent(webEvent);
            this.disableTextInput();
        }
        ErrorReporter.capture(e);
        console.error(e);
    }

    getNTEContainerBounds() {
        const bounds = this._pixiContainer.getBounds().clone();

        if (this.renderer.metrics) {
            const { xOffset, yOffset } = this._calculateGraphicsOffsetFromText();
            bounds.x += xOffset;
            bounds.y += yOffset;

            const fullContainerWidth = Math.max(
                this.renderer.activeTextBounds!.width,
                this.renderer.activeTextMaxLineWidth,
            );
            bounds.width = fullContainerWidth / this.referenceToOutputMultiplier;
            bounds.height =
                this.renderer.activeTextBounds!.height / this.referenceToOutputMultiplier;
        }
        return bounds;
    }

    /**
     * Determines if an arbitrary text value and optionally a set of different styles
     * can be fully displayed or not.
     *
     * `wordBreak: false` treats a word that overflows `maxWidth` as not fitting, so the
     * caller (e.g. `setFontSizeToFitAllText`) can shrink the text instead of relying on
     * the renderer to force-break the word across lines.
     */
    canFitText(
        newText: string = '',
        newStyles: TextEditingStyles = {},
        { wordBreak = true }: { wordBreak?: boolean } = {},
    ): boolean {
        // Overflow is undetectable without a measuring context, so report the non-clipping answer.
        if (!canMeasureText()) {
            return true;
        }
        return this.#measureWithOwnStyles({
            text: newText,
            fontSize: newStyles?.fontSize || this.textSize,
            wordBreak,
        }).canDisplayAllText;
    }

    #measureWithOwnStyles({
        text,
        fontSize = this.textSize,
        wordBreak = true,
        ignoreMaxHeight = false,
    }: {
        text: string;
        fontSize?: number;
        wordBreak?: boolean;
        ignoreMaxHeight?: boolean;
    }): TextMetrics {
        const leading = fontSize * this.leadingMultiplier;
        const rendererSettings = this.getRendererSettings();
        // TODO: we manually build a TextStyle here, same confusing pattern as getFontSizeToFitAllText. We should refactor this.
        const normalStyle = new TextStyleRenderer().apply(
            this.normalStyle.getRendererSettings({
                leading,
                fontSize,
            }),
        );
        const highlightStyle = new TextStyleRenderer().apply(
            this.highlightStyle.getRendererSettings({
                leading,
                fontSize: fontSize * this.highlightedTextScale,
            }),
        );

        return buildMetricsForText({
            text: unicodeToChar(text),
            alignment: rendererSettings.alignment,
            normalStyle,
            highlightStyle,
            highlightAreas: rendererSettings.highlightAreas,
            boldRanges: rendererSettings.boldRanges,
            italicRanges: rendererSettings.italicRanges,
            underlineRanges: rendererSettings.underlineRanges,
            wordBreak,
            maxWidth: rendererSettings.maxTextWidth,
            maxHeight: ignoreMaxHeight ? Infinity : rendererSettings.maxTextHeight,
            referenceToOutputMultiplier: this.referenceToOutputMultiplier,
        });
    }

    /** Height of every line of the text, in scene-reference units, as if `maxHeight` did not clip it. */
    get unclippedHeight(): number {
        if (!canMeasureText()) {
            return 0;
        }
        return (
            this.#measureWithOwnStyles({ text: this.text, ignoreMaxHeight: true }).bounds.height /
            this.referenceToOutputMultiplier
        );
    }

    #captureCircleRadiusPaddings(outputHalfDiagonal: number): void {
        this.#stylesList.forEach((style) =>
            style.graphics.forEach((graphic) => {
                if (graphic instanceof TextAssetDynamicCircle) {
                    graphic._captureRadiusPadding(
                        outputHalfDiagonal / graphic.referenceToOutputMultiplier,
                    );
                }
            }),
        );
    }

    /**
     * Invokes the build process of the text editing manager. This method should be included
     * in any build loop so that the text editing input stays up to date.
     */
    updateTextInput() {
        if (!this.hasTextEditingEnabled) {
            return;
        }
        try {
            const timeParent = this.subscene ?? this._requireSlide;
            this._seek(timeParent._timeline.progress, SeekMode.ResolveWhenSingleFrame);

            this.#textEditingManager?.updateCaretColor();
        } catch (e) {
            this.#handleEditingException(e);
        }
    }

    override destroy(): void {
        this.#textEditingManager?.destroy({ assetBeingDestroyed: true });
        super.destroy();
        this.destroyDynamicStyles();
    }

    /**
     * Returns an object that includes the character index of the closest character to
     * the location passed in and the distance to that index
     */
    getClosestTextIndex(coordinate: Coordinate): number {
        if (
            !coordinate ||
            typeof coordinate.x !== 'number' ||
            typeof coordinate.y !== 'number' ||
            coordinate.x < 0 ||
            coordinate.y < 0
        ) {
            return 0;
        }

        // if there's no text, put the cursor at the start
        if (this.text.length === 0) {
            return 0;
        }

        const metrics = this.renderer.metrics;
        if (!metrics) {
            return 0;
        }

        // This factor allows the click to give preference to the current line, by virtually
        // separating lines from each other. This is obvious when an adjacent line is longer
        // than the current line. If a click happens behind the shorter line then the closest
        // char will be in the adjacent line, but it's more natural to put the cursor at the end
        // of the clicked line.
        const yScale = 10;
        coordinate.y *= yScale;

        let minDistance = Infinity;
        let indexToMinDistance = 0;
        const textAssetPosition = this._pixiContainer.getGlobalPosition();

        // note that this loop goes from 0 to _after_ the last
        // character - we are checking each letter, before and after
        // so be careful not to trigger an index exception in here
        for (let i = 0; i <= this.text.length; i++) {
            // get the line, word and character hierarchy index for the current character
            const offset = metrics.getOffsetsForChar(i);

            // Make sure the PIXI objects actually exist
            if (offset.lineIndex >= this._wordElements.length) {
                continue;
            }
            if (offset.wordIndex >= this._wordElements[offset.lineIndex].length) {
                continue;
            }

            // get the position (Point) of the word the character belongs to
            const textLine = metrics.lines[offset.lineIndex];
            const wordElement =
                this._wordElements[offset.lineIndex][offset.wordIndex][offset.elementIndex];

            // get the position (Point) of the character relative to the word
            const relCharacterPosition = metrics.positionOfCharRelativeToWordElement(
                offset.lineIndex,
                offset.wordIndex,
                offset.elementIndex,
                offset.characterIndex,
            );

            // add the character offset to the position of the word to get the global character position
            const lineHeight =
                metrics.lines[offset.lineIndex].height / this.referenceToOutputMultiplier;
            const characterPosition = new PIXI.Point(
                textAssetPosition.x +
                    wordElement.bounds.x / this.referenceToOutputMultiplier +
                    relCharacterPosition.x / this.referenceToOutputMultiplier,
                (textAssetPosition.y +
                    textLine.bounds.y / this.referenceToOutputMultiplier +
                    lineHeight / 2) *
                    yScale,
            );

            // collect the index of the character closest to the pixel location
            const charDistance = distance(characterPosition, coordinate);
            if (charDistance < minDistance) {
                minDistance = charDistance;
                indexToMinDistance = i;
            }
        }

        return indexToMinDistance;
    }

    destroyDynamicStyles() {
        this.#stylesList.forEach((style) => {
            style.removeDynamicStyles();
        });
    }

    override _build(): void {
        super._build();

        const metrics = this.renderer.metrics;
        const metricsDescribeCurrentText =
            metrics !== null && !this.inEmptyState && this.isDoneTryingToLoad;
        if (metricsDescribeCurrentText) {
            this.#captureCircleRadiusPaddings(getHalfDiagonal(metrics.bounds));
        }

        this.animationManager.clear();
        this.updateTransform();
        if (this.hasMorphAnimation) {
            this._updateMorphAnimation();
        }
        const configs = this.renderer.buildAnimationManagerConfig(this.container.duration);
        configs.forEach((config) => {
            if (this.isMatchCutFrozen) {
                holdConfigCurveAtRest(config);
            } else {
                MorphManager.modifyNonMorphCurve(
                    config,
                    this.hasMorphLeadInAnimation,
                    this.hasMorphAnimation,
                );
            }
            this.animationManager.add(config);
        });

        this._applyIntroAnimations();
        this._applyOutroAnimations();
        this.animationManager.setInitValues();
    }

    override _seek(progress: number, seekMode: SeekMode) {
        if (progress >= 0 && progress <= 1) {
            this.animationManager.percentage = progress;
        }

        super._seek(progress, seekMode);
    }

    _syncFontSizeAndLeadingToStyles() {
        this.normalStyle.fontSize = this.textSize;
        this.highlightStyle.fontSize = this.textSize * this.highlightedTextScale;

        // set leading based on the textSize
        const leading = this.textSize * this.leadingMultiplier;
        this.normalStyle.leading = leading;
        this.highlightStyle.leading = leading;
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat([
            ...getAttributesToSerialize(this),
            'createdFrom',
        ]);
    }

    override get editableProperties() {
        return super.editableProperties.concat([
            'text',
            'textHighlights',
            'textBold',
            'textItalic',
            'textUnderline',
            'normalStyle',
            'highlightStyle',
        ]);
    }

    override _loadStylePropertiesForSwap(data: RawAssetData) {
        super._loadStylePropertiesForSwap(data);

        if (data.classType === Config.FANCY_CAPTION_ASSET_TYPE) {
            const style = data.style as { fontUrl: string; fill: number };
            this.normalStyle.fontUrl = style.fontUrl;
            this.normalStyle.fill = style.fill;
            this.highlightStyle.fill = style.fill;
            this.textHighlights = [];
        }
    }
}

export const sanitizeHighlights = (text: string, ranges: [number, number][]) => {
    // discard any ranges with an invalid start index
    const filteredRanges = ranges.filter((range) => {
        return range[0] < range[1] && range[0] >= 0 && range[0] < text.length;
    });

    // for any range that exists past the text's length, cap it at the length
    const cappedRanges: [number, number][] = filteredRanges.map((range) => {
        if (range[1] > text.length) {
            return [range[0], text.length];
        }
        return range;
    });

    // sort by start index ascending
    const sortedRanges = cappedRanges.sort((a, b) => a[0] - b[0]);

    // merge any overlapping ranges
    for (let i = 1; i < sortedRanges.length; i++) {
        const firstRangeIdx = i - 1;
        const firstRange = sortedRanges[firstRangeIdx];
        const secondRange = sortedRanges[i];

        const hasOverlap = secondRange[0] <= firstRange[1] && secondRange[1] >= firstRange[0];
        if (!hasOverlap) {
            continue;
        }

        // replace the two compared ranges with a merged version
        const allIndices = [...firstRange, ...secondRange];
        const mergedRange: [number, number] = [Math.min(...allIndices), Math.max(...allIndices)];
        const spliceArgs: [number, number, ...[number, number][]] = [firstRangeIdx, 2];
        if (mergedRange[1] - mergedRange[0] > 0) {
            spliceArgs.push(mergedRange); // the range must span at least one char
            i--;
        }
        sortedRanges.splice(...spliceArgs);
    }

    return sortedRanges;
};
